import {DrawFrmApp} from "../../drawfrm/DrawFrmApp";
import {ManagedScene2} from "../ManagedScene2";
import {FrmSceneLayout} from "../../drawfrm/FrmSceneLayout";
import {ElevationApp} from "../../ElevationApp";
import {AddFx, ChildView} from "../../shared-2d/ChildView";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {FrmVar} from "../../drawfrm/FrmVar";
import {DoorLeaf} from "../doorElev/DoorLeaf";
import {StickHost, StickMaterial} from "../frameGrid/StickMaterial";
import {LinearDimension} from "../frameGrid/LinearDimension";
import {HardwareCrosshairThing} from "../hardware/HardwareCrosshairThing";
import {placedOnLeaf} from "../hardware/hardwarePreps";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {MdHinge} from "../../model/hardware/MdHinges";
import {HingeGeom, HingeThing} from "./HingeThing";
import {faceCaption, faceMirrored, faceSwingsOut, hingeSideOrDefault, OpeningFace} from "../frameProfile/Helpers";
import {inches, lineBlueHeavy} from "../../const";
import {itemFace} from "../../model/hardware/MdHardwareRules";
import {Label} from "../../shared-2d/Label";
import {SwingPreview} from "../frameGrid/item/DoorFace";
import {Rectangle} from "pixi.js";

// how far the hinge-spacing dimensions sit outboard of the hinge edge, past the
// jamb, so they read clear of the frame geometry (scene units = mm).
const HINGE_DIM_CLEARANCE = 4 * inches;

// how far the frame-undercut dimension sits outboard of the left jamb's outer face
// (scene units = mm).
const UNDERCUT_DIM_CLEARANCE = 2 * inches;

// the face caption over each view, its clearance above the frame, and the space
// between the two faces (scene units = mm).
const CAPTION_FONT = 60;
const CAPTION_GAP = 6 * inches;
const FACE_GAP = 30 * inches;

// One face of the "Locations" view: a single door leaf (active or inactive) shown inside
// a frame open at the floor — head plus jambs — with its hinges, handle and deadbolt,
// and editable dimensions chaining each hinge center down from the top of the frame
// jamb. A single door gets both jambs; a pair leaf gets only its hinge-side jamb (the
// other edge meets the opposite leaf). `active` selects which leaf of a pair is shown;
// `face` which side of the opening it's seen from (the back face is the mirror image
// and shows only the hardware mounted on it, see itemFace). The scene shows both faces
// side by side inside one container it scales as a unit (see LocationsScene.arrange).
//
// It reuses the door-elevation building blocks: DoorLeaf (leaf face + hinges) and
// HardwareCrosshairThing (handle / lock / any placed door hardware). The three
// jamb sticks are inert StickMaterials (purely visual — no select/resize), owned
// by this view (which acts as their StickHost). The hinge dimensions are a chain
// of LinearDimensions: the first spans the top of the frame jamb to the first
// hinge center, each subsequent one spans center-to-center.
class LocationsView extends ChildView implements StickHost {
    active: boolean;
    face: OpeningFace;

    // the caption over the face, placed above parentThing by placeAt.
    private caption: Label;

    // the hover swing preview (badge + ghost leaf), as on the frame grid's door face.
    private swing = new SwingPreview();

    // the shared container holding the leaf + the three jamb sticks. Positioned by
    // the scene layout; the leaf sits at its local origin so hardware crosshairs and
    // hinge dimensions (children of the leaf) stay in leaf-local coordinates.
    parentThing: DrawFrmThing;
    leaf: DoorLeaf;

    // StickHost: the jamb sticks. They're inert, so nothing here reads `sticks`, but
    // the interface requires it (FrameGridView uses it for resize snapping).
    sticks: StickMaterial[] = [];
    private headStick: StickMaterial;

    // the head runs the full outer width, which depends on the leaf width + jamb
    // faces; recomputed in layoutFrame. The jambs run the frame height (nominalHeight);
    // any frame undercut is drawn as a red hatch band at their bottom by StickMaterial.
    private headLength = new FrmVar("locations.headLength", 0);

    // placed door hardware crosshairs (handle, lock, and any door-prepped item),
    // children of the leaf. Rebuilt when the placed-hardware list changes.
    private crosshairThings: HardwareCrosshairThing[] = [];

    // one selectable knuckle per hinge, children of the leaf. Rebuilt when the hinge
    // count changes (the leaf itself draws no knuckles — see interactiveHinges).
    private hingeThings: HingeThing[] = [];

    // one dimension per hinge, children of the leaf, rebuilt to match the hinge count.
    private hingeDims: LinearDimension[] = [];

    // the editable frame-undercut dimension, spanning the jamb's red undercut band.
    // Drawn only when the undercut is non-zero (see drawUndercutDim); a child of
    // parentThing so it reads in frame (not leaf) coordinates.
    private undercutDim!: LinearDimension;

    constructor(elev: ElevationApp, add: AddFx, active: boolean, face: OpeningFace) {
        super(elev, add);
        this.active = active;
        this.face = face;

        this.caption = new Label({text: faceCaption(elev, face), fontSize: CAPTION_FONT, color: lineBlueHeavy, anchorX: 0.5, anchorY: 1});
        this.add(this.caption);

        this.parentThing = new DrawFrmThing();
        this.add(this.parentThing);

        this.leaf = new DoorLeaf(elev, active, {showCuts: true, interactiveHinges: true, mirror: () => this.mirrored()});
        this.parentThing.add(this.leaf);

        // the three inert jamb sticks (head + left + right).
        const fs = elev.frameSpec;
        this.headStick = new StickMaterial(elev, {
            id: "locations:head", view: this, orientation: "horizontal",
            length: this.headLength, thickness: fs.jambFace, interactive: false,
        });
        const leftJamb = new StickMaterial(elev, {
            id: "locations:jamb-left", view: this, orientation: "vertical",
            length: fs.nominalHeight, thickness: fs.jambFace, interactive: false,
            undercut: fs.undercutFrame, undercutHatch: true,
        });
        const rightJamb = new StickMaterial(elev, {
            id: "locations:jamb-right", view: this, orientation: "vertical",
            length: fs.nominalHeight, thickness: fs.jambFace, interactive: false,
            undercut: fs.undercutFrame, undercutHatch: true,
        });
        this.sticks = [this.headStick, leftJamb, rightJamb];
        for (const s of this.sticks) this.parentThing.add(s);

        this.undercutDim = new LinearDimension(this.parentThing.lines, elev.draw);

        this.buildHardwareItems();

        // rebuild the door crosshairs when the placed-hardware list changes.
        this.trackSubscription(elev.hardware.listChanged.subscribe(() => {
            this.buildHardwareItems();
            this.redraw();
        }));

        // hinge edits (count/spacing/Auto) re-emit listChanged; redraw to reflect them.
        this.trackSubscription(elev.hardware.hinges.listChanged.subscribe(() => this.redraw()));

        // any input that moves the leaf or frame geometry re-flows the whole view.
        const relayout = () => {
            this.redraw();
            this.elev.draw.currentScene?.applyLayout();
        };
        this.trackSubscription(elev.doorSpec.actualHeight.subscribe(relayout));
        this.trackSubscription((active ? elev.doorSpec.activeDoorWidth : elev.doorSpec.inActiveDoorWidth).subscribe(relayout));
        this.trackSubscription(elev.frameSpec.jambFace.subscribe(relayout));
        this.trackSubscription(elev.frameSpec.nominalHeight.subscribe(relayout));
        this.trackSubscription(elev.frameSpec.undercutFrame.subscribe(relayout));
        this.trackSubscription(elev.frameSpec.frameJambToDoorGap.subscribe(relayout));

        // toggle hardware/hinge dimensions with the "Show All Dimensions" view setting.
        this.trackSubscription(elev.viewSettings.dimShowAll.subscribe(() => this.redraw()));

        this.redraw();
    }

    // StickHost: an inert stick never calls this (it's the resize re-solve hook), but
    // the interface requires it. Redraw in case a future caller reaches for it.
    render() {
        this.redraw();
    }

    private mirrored(): boolean {
        return faceMirrored(this.elev, this.face);
    }

    private hingesOnLeft(): boolean {
        return (hingeSideOrDefault(this.elev, this.active) === "left") !== this.mirrored();
    }

    // position the leaf + the three jambs, and size the head to span the outer
    // width. The leaf sits at the parent origin; the frame wraps it: the head's
    // underside is at y=0 (the top of the jamb), the jambs run down from there, and
    // each jamb's inner face is a reveal off the leaf edge.
    private layoutFrame() {
        const fs = this.elev.frameSpec;
        const jambFace = fs.jambFace.get();
        const reveal = fs.frameJambToDoorGap.get();
        const LW = this.leaf.leafWidth();

        this.leaf.graphic.position.set(0, 0);

        const leftJamb = this.sticks[1];
        const rightJamb = this.sticks[2];

        // left jamb: inner face a reveal left of the leaf; right jamb: a reveal right.
        leftJamb.graphic.position.set(-reveal - jambFace, 0);
        rightJamb.graphic.position.set(LW + reveal, 0);

        // a pair leaf carries a jamb only on its hinge side; the opposite edge is the
        // meeting (astragal) edge against the other leaf, with no jamb there. A single
        // door keeps both jambs. The head then spans from the jamb's outer face to the
        // leaf's open edge: the meeting edge for a pair, the far jamb's outer face for
        // a single.
        if (fs.isPair) {
            const onLeft = this.hingesOnLeft();
            leftJamb.graphic.visible = onLeft;
            rightJamb.graphic.visible = !onLeft;

            const headX = onLeft ? -reveal - jambFace : 0;
            this.headStick.graphic.position.set(headX, -jambFace);
            this.headLength.set(LW + reveal + jambFace, {debug: "locations.headLength"});
        } else {
            leftJamb.graphic.visible = true;
            rightJamb.graphic.visible = true;

            this.headStick.graphic.position.set(-reveal - jambFace, -jambFace);
            this.headLength.set(LW + 2 * reveal + 2 * jambFace, {debug: "locations.headLength"});
        }
    }

    // (re)build the door crosshair Things from the placed-hardware list (door
    // placements only). Destroy old ones first so removed items leave no orphan.
    private buildHardwareItems() {
        for (const t of this.crosshairThings) {
            if (!t.destroyed) t.destroy();
        }
        this.crosshairThings = [];

        const hw = this.elev.hardwareGroup?.hardware ?? [];
        // this leaf's own door placements (an item on both leaves has one per leaf).
        for (const placement of this.elev.hardware.list.filter(p => placedOnLeaf(p, this.active))) {
            const item = hw.find(h => h.product === placement.productId);
            if (!item) continue; // product not in the group anymore — skip

            // hardware mounted on the other face isn't visible from this one.
            const face = itemFace(item);
            if (face && face !== this.face) continue;

            const thing = new HardwareCrosshairThing({
                elev: this.elev,
                item: placement,
                label: item.productCode.slice(0, 6),
                product: item,
                mirror: () => this.mirrored(),
                target: {rect: () => ({x0: 0, y0: 0, x1: this.leaf.leafWidth(), y1: this.leaf.leafHeight()})},
                onSelect: (additive) => this.selectHardwareItem(placement, additive),
            });
            this.leaf.lines.addChild(thing.graphic);
            this.crosshairThings.push(thing);
        }
    }

    private selectHardwareItem(item: MdHardwarePlacement, additive: boolean) {
        this.elev.viewSettings.selectItem({type: "hardware-item", id: item.id, kind: item.target, productId: item.productId}, additive);
        this.elev.viewSettings.activeTab.emit("selected");
    }

    // (re)build the selectable hinge knuckles, one per hinge. Only rebuilds when the
    // count changes (a spacing/override edit keeps the same Things and just redraws),
    // so the selection subscription inside each HingeThing survives ordinary edits.
    private buildHinges() {
        const list = this.elev.hardware.hinges.list;
        if (this.hingeThings.length === list.length) return;

        for (const t of this.hingeThings) {
            if (!t.destroyed) t.destroy();
        }
        this.hingeThings = [];

        list.forEach((h, i) => {
            const thing = new HingeThing({
                elev: this.elev,
                hinge: h,
                leaf: this.active ? "active" : "inactive",
                geom: () => this.hingeGeom(i),
                onSelect: (additive) => this.selectHinge(h, additive),
            });
            this.leaf.lines.addChild(thing.graphic);
            this.hingeThings.push(thing);
        });
    }

    // the knuckle placement for hinge `i`, in leaf-local coords. Mirrors the hinge
    // edge/height math the leaf used to draw inline (offsetTop down the hinge edge).
    private hingeGeom(i: number): HingeGeom {
        const onLeft = this.hingesOnLeft();
        return {
            edgeX: onLeft ? 0 : this.leaf.leafWidth(),
            cy: this.elev.hardware.hinges.list[i].offsetTop.get(),
            hh: this.elev.hardware.hinges.hingeHeightAt(i),
            outboard: onLeft ? -1 : 1,
        };
    }

    private selectHinge(hinge: MdHinge, additive: boolean) {
        this.elev.viewSettings.selectItem({type: "hinge", id: `hinge:${hinge.id}`, hingeId: hinge.id}, additive);
        this.elev.viewSettings.activeTab.emit("selected");
    }

    // grow/shrink the dimension pool to one per hinge.
    private buildHingeDims() {
        const need = this.elev.hardware.hinges.list.length;
        while (this.hingeDims.length > need) {
            this.hingeDims.pop()!.destroy();
        }
        while (this.hingeDims.length < need) {
            this.hingeDims.push(new LinearDimension(this.leaf.lines, this.elev.draw));
        }
    }

    // draw the chained hinge dimensions down the hinge edge: the first from the top
    // of the frame jamb to the first hinge center, each subsequent one center-to-
    // center. Editing a dimension sets that hinge's spaceAbove and re-solves the
    // chain (downstream hinges ride along).
    private drawHingeDims() {
        const hinges = this.elev.hardware.hinges;
        const list = hinges.list;
        const fs = this.elev.frameSpec;

        const onLeft = this.hingesOnLeft();
        const dir = onLeft ? -1 : 1;
        const hingeEdge = onLeft ? 0 : this.leaf.leafWidth();
        const cross = hingeEdge + dir * (fs.frameJambToDoorGap.get() + fs.jambFace.get() + HINGE_DIM_CLEARANCE);

        list.forEach((h, i) => {
            const to = h.offsetTop.get();
            const from = i === 0 ? 0 : list[i - 1].offsetTop.get();

            this.hingeDims[i].draw({
                axis: "vertical",
                from, to, cross,
                value: h.spaceAbove.get(),
                modified: h.spaceAbove.isModified(),
                onSet: v => {
                    h.spaceAbove.userSet(v, {debug: `hinge[${h.id}].spaceAbove`});
                    hinges.recomputeOffsets();
                    this.elev.draw.currentScene?.applyLayout();
                },
                onClear: () => {
                    h.spaceAbove.set(hinges.autoSpaceAboveFor(i), {debug: `hinge[${h.id}].spaceAbove.clear`});
                    h.spaceAbove.clearModified();
                    hinges.recomputeOffsets();
                    this.elev.draw.currentScene?.applyLayout();
                },
            });
        });
    }

    // draw the editable frame-undercut dimension over the jamb's red undercut band
    // (the bottom `undercut` of the jamb), outboard of the left jamb. Hidden when
    // there's no undercut. Editing it sets frameSpec.undercutFrame.
    private drawUndercutDim() {
        const fs = this.elev.frameSpec;
        const undercut = fs.undercutFrame.get();
        if (undercut <= 0) {
            this.undercutDim.hide();
            return;
        }

        const nominal = fs.nominalHeight.get();
        // sit the dimension just outboard of the jamb carrying the undercut. For a pair
        // that's the single hinge-side jamb (the right jamb when the hinges are on the
        // right); otherwise the left jamb's outer face (x = -reveal - jambFace).
        const onRightJamb = fs.isPair && !this.hingesOnLeft();
        const cross = onRightJamb
            ? this.leaf.leafWidth() + fs.frameJambToDoorGap.get() + fs.jambFace.get() + UNDERCUT_DIM_CLEARANCE
            : -fs.frameJambToDoorGap.get() - fs.jambFace.get() - UNDERCUT_DIM_CLEARANCE;

        this.undercutDim.draw({
            axis: "vertical",
            from: nominal - undercut, to: nominal, cross,
            value: undercut,
            modified: fs.undercutFrame.isModified(),
            onSet: v => {
                fs.undercutFrame.userSet(v, {debug: "frameSpec.undercutFrame"});
                this.elev.draw.currentScene?.applyLayout();
            },
            onClear: () => {
                fs.undercutFrame.set(0, {debug: "frameSpec.undercutFrame.clear"});
                fs.undercutFrame.userClearModified();
                this.elev.draw.currentScene?.applyLayout();
            },
        });
    }

    redraw() {
        this.layoutFrame();
        this.leaf.draw();
        this.buildHingeDims();
        this.drawHingeDims();
        this.drawUndercutDim();
        this.buildHinges();
        for (const t of this.hingeThings) {
            t.draw();
        }
        for (const t of this.crosshairThings) {
            t.draw();
        }
        this.updateSwing();
    }

    // refresh the swing preview's leaf box: the leaf face (below any overcut), pivoting on
    // this face's hinge edge and opening toward the viewer per the face's swing.
    private updateSwing() {
        const overcut = this.elev.doorSpec.overcut.get();
        this.swing.update({dimensionContainer: this.parentThing.graphic, elev: this.elev}, [{
            doorLeft: 0,
            doorRight: this.leaf.leafWidth(),
            doorTop: overcut,
            doorBottom: overcut + this.leaf.leafHeight(),
            cut: 0,
            hingesOnLeft: this.hingesOnLeft(),
            swingsOut: faceSwingsOut(this.elev, this.face, this.active),
            lites: [],
        }]);
    }

    // the extent of the frame, leaf and dimensions in parentThing's local (mm) space.
    contentBounds(): Rectangle {
        return this.parentThing.graphic.getLocalBounds();
    }

    // put the leaf origin at `x` in the container this view was added to, and centre
    // the caption over the content.
    placeAt(x: number) {
        const b = this.contentBounds();
        this.parentThing.graphic.position.set(x, 0);
        this.caption.graphic.position.set(x + b.x + b.width / 2, b.y - CAPTION_GAP);
    }

    destroy() {
        this.swing.destroy();
        // the leaf's children (hardware crosshairs + hinge dimensions) are freed as
        // pixi graphics when the scene tears the leaf down, but their Thing.destroy()
        // (subscription cleanup) won't run that way — destroy them explicitly. Both
        // guard against a double free, so this is safe after the scene's teardown.
        for (const t of this.crosshairThings) {
            if (!t.destroyed) t.destroy();
        }
        this.crosshairThings = [];
        for (const t of this.hingeThings) {
            if (!t.destroyed) t.destroy();
        }
        this.hingeThings = [];
        for (const d of this.hingeDims) {
            d.destroy();
        }
        this.hingeDims = [];
        this.undercutDim.destroy();
        super.destroy();
    }
}

export class LocationsScene extends ManagedScene2 {
    private active: boolean;

    // both faces live in this one container, in mm, so the layout fits and scales
    // them together and the gap between them stays in door units.
    private root = new DrawFrmThing();
    private views: LocationsView[] = [];

    constructor(app: ElevationApp, active: boolean) {
        super(app);
        this.active = active;
    }

    setup(app: DrawFrmApp): void {
        super.setup(app);
        this.add(this.root);

        // both faces of the leaf, side by side: the front (from side) then the back (to side).
        const add: AddFx = item => this.root.add(item);
        this.views = [
            this.track(new LocationsView(this.elev, add, this.active, "front")),
            this.track(new LocationsView(this.elev, add, this.active, "back")),
        ];

        this.setupLayout(
            FrmSceneLayout.column(
                FrmSceneLayout.group([this.root]).grow(1),
            )
                .grow(1)
                .padding(20),
        );
    }

    applyLayout() {
        this.arrange();
        super.applyLayout();
    }

    // lay the faces out left to right inside root, each a FACE_GAP past the previous
    // one's content, before the layout fits root to the viewport.
    private arrange() {
        let x = 0;
        for (const view of this.views) {
            const b = view.contentBounds();
            view.placeAt(x - b.x);
            x += b.width + FACE_GAP;
        }
    }
}
